import React, { useState } from 'react';
import classes from 'src/components/CategoryListingPage/ProductList.scss';
import { Link } from 'react-router';
import { LazyImg } from 'src/hooks/useLazyLoad';
import { Svg } from 'src/components/Svg/Svg';
import { Price } from 'src/components/Helpers/Price';
import { TagsAggregation } from 'src/components/CategoryListingPage/utils/cleanAggregations';
import { RenderTag, AlgoliaTag } from 'src/components/CategoryListingPage/AlgoliaTag';
import { ProductListColourPicker } from 'src/components/CategoryListingPage/ProductListColourPicker';
import classnames from 'classnames';
import { getPercentage } from 'src/helpers/getPercentage';
import { VatInfo } from 'src/components/VatInfo/VatInfo';
import { optionsAndVariants } from 'src/components/ProductDetail/utils/product-options';
import { ColourPickerSwatch, optionsToColorSwatches } from 'src/components/ProductDetail/utils/product-detail.types';
import { CamelizedSwatch } from 'src/components/ProductDetail/ProductSwatch';
import camelcaseKeys from 'camelcase-keys';
import { productOptions_productOptions_items_ConfigurableProduct as Options } from 'src/queries/__generated__/productOptions';
import { FREE_SHIPPING } from 'src/constants/meta';
import { ALLOWED_MULTIPACK_TAGS } from 'src/constants/productList';

import { ProductListItem as ProductListItemType } from './utils/productListItem';

type ItemProps = {
    product: ProductListItemType;
    loading: boolean;
    gtmSelectItem(index: number): void;
    index: number;
    vat: boolean;
    onChange?: (checked: boolean, code: string, label: string) => void;
    tagsSelected?: (string | false | null)[];
    tagsAggregation: TagsAggregation[];
    algolia?: boolean;
    colorSelections: string[];
    onAction?: (id: number, name: string, options?: any, image?: string) => void;
    multipackSelected?: string[];
};

enum CustomisationTags {
    PRINT = 'print available',
    EMBROIDERY = 'embroidery available',
}

export const checkForBestsellerTag = (
    productTagsSorted: string[],
    tagsAggregation: TagsAggregation[],
    algolia?: boolean,
) => {
    if (algolia) {
        return productTagsSorted.includes('Bestseller');
    }

    if (!algolia) {
        const selectedTagNames = productTagsSorted.map(
            (tag) => tagsAggregation.find((aggregation) => aggregation.value === tag)?.label,
        );

        return selectedTagNames.includes('Bestseller');
    }
};

export const filterBestsellerTag = (
    productTagsSorted: string[],
    tagsAggregation: TagsAggregation[],
    algolia?: boolean,
) => {
    if (algolia) return productTagsSorted.filter((tag) => tag !== 'Bestseller');

    if (!algolia) {
        const tagIndex = productTagsSorted
            .map((tag) => tagsAggregation.find((aggregation) => aggregation.value === tag)?.label)
            .indexOf('Bestseller');

        if (tagIndex !== -1) productTagsSorted.splice(tagIndex, 1);

        return productTagsSorted;
    }
};

export const ProductListItem: React.FC<ItemProps> = ({
    product,
    loading,
    gtmSelectItem,
    index,
    vat,
    onChange,
    tagsSelected,
    tagsAggregation,
    algolia,
    colorSelections,
    onAction,
    multipackSelected = [],
}) => {
    const [currentColor, setCurrentColor] = useState<ColourPickerSwatch | null>(null);
    const [defaultColor, setDefaultColor] = useState<ColourPickerSwatch | null>(null);
    const [isHovered, setIsHovered] = useState<boolean>(false);

    const [options, setOptions] = useState<Options | null>(null);

    const sortedAggregationTagsGetValues = [...tagsAggregation]
        .sort((a, b) => a.label.length - b.label.length)
        .map((el) => el.value);

    const currentUrl = product.url.split('?')[0];

    // Some product_tags come as a string, make an array
    let productTags: string[] = product.product_tags;
    if (typeof product.product_tags === 'string') {
        productTags = [product.product_tags];
    }

    // Sort from smallest to biggest
    const productTagsSorted =
        (productTags &&
            [...productTags].sort(
                (a, b) => sortedAggregationTagsGetValues.indexOf(a) - sortedAggregationTagsGetValues.indexOf(b),
            )) ||
        [];

    const prices = product.price;

    const showDiscountPrice = Boolean(
        Number(prices.previous_prices?.price_exl) && Number(prices.previous_prices?.price_inc),
    );

    const brandName = product.brand.toString().toLowerCase();

    const productName = product.name.toLowerCase().includes(brandName)
        ? product.name.toLowerCase().replace(brandName, '')
        : product.name;

    const productHasBestsellerTag = checkForBestsellerTag(productTagsSorted, tagsAggregation, algolia);
    const productHasMultipackTag =
        (product.multipackInfo?.label !== null &&
            product.multipackInfo?.label !== undefined &&
            ALLOWED_MULTIPACK_TAGS.includes(product.multipackInfo?.label)) ||
        ((product.multipackInfo === null || product.multipackInfo === undefined) &&
            product.multipack !== null &&
            product.multipack !== undefined &&
            ALLOWED_MULTIPACK_TAGS.includes(product.multipack.toString()));

    const filteredTags = filterBestsellerTag(productTagsSorted, tagsAggregation, algolia);

    const filteredTagsLabels = tagsAggregation
        .filter((tag) => filteredTags?.includes(tag.value))
        .map((tag) => tag.label.toLowerCase());

    const productIsCustomisable =
        filteredTagsLabels.length > 0
            ? filteredTagsLabels.includes(CustomisationTags.PRINT) ||
              filteredTagsLabels.includes(CustomisationTags.EMBROIDERY)
            : Boolean(
                  filteredTags?.find(
                      (tag) =>
                          tag.toLowerCase() === CustomisationTags.PRINT ||
                          tag.toLowerCase() === CustomisationTags.EMBROIDERY,
                  ),
              );

    const mainContentImage = () => {
        let displayImage = product.small_image.url;

        if (currentColor) {
            displayImage =
                isHovered && currentColor.swatch_image_url
                    ? currentColor.swatch_image_url
                    : currentColor.product_small_url;
        } else if (defaultColor?.swatch_image_url && isHovered) {
            displayImage = defaultColor.swatch_image_url;
        }

        return (
            <>
                {productIsCustomisable && (
                    <LazyImg
                        src={require('../../../static/img/addIcon.svg')}
                        alt="Add your logo"
                        className={classes.productListIcon}
                    />
                )}
                {showDiscountPrice && (
                    <div className={classes.discount}>
                        -{Math.abs(getPercentage(Number(prices.previous_prices?.price_exl) || 0, prices.price))}%
                    </div>
                )}
                {product.is_24_hour_delivery && <Svg className={classes.productListDelivery} name="24hr" />}

                {(product.is_bestseller || productHasBestsellerTag) && (
                    <small className={classes.productListBestseller}>
                        <Svg name="star" /> Bestseller
                    </small>
                )}
                {productHasMultipackTag && (
                    <div
                        className={classnames(classes.productListMultipack, {
                            [classes.withBestseller]: product.is_bestseller || productHasBestsellerTag,
                        })}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (
                                onChange &&
                                product.multipackInfo?.id !== undefined &&
                                product.multipackInfo?.id !== null
                            ) {
                                const multipackId = product.multipackInfo.id.toString();
                                const isSelected = multipackSelected.includes(multipackId);
                                onChange(!isSelected, 'multipack', multipackId);
                            }
                        }}
                    >
                        <LazyImg
                            className={classes.image}
                            src={require(
                                `../../../static/icons/${
                                    product.multipackInfo?.label || product.multipack
                                }PairPack.png`,
                            )}
                            alt={`${product.multipackInfo?.label || product.multipack} pair pack`}
                        />
                    </div>
                )}
                <img
                    src={displayImage}
                    alt={product.small_image.label || product.name}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                />
                <link itemProp="image" href={product.small_image.url} />
            </>
        );
    };

    const colorOptions = options?.configurable_options && options.variants ? optionsAndVariants(options) : undefined;

    const colors = optionsToColorSwatches(colorOptions?.options);
    const camelizedColors = camelcaseKeys(colors, { deep: true }) as CamelizedSwatch[];

    const productValues = {
        colors: [...camelizedColors],
        sizes: [product?.configurable_options?.[1].values],
    };

    return (
        <li
            key={product.id}
            className={classes.productListItem}
            data-test-id="ProductItem"
            data-item-loading={String(loading)}
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/Product"
        >
            <meta itemProp="description" content={product.name} />
            <div className={classes.productListContent}>
                {onAction ? (
                    <div
                        className={classes.productListImage}
                        onClick={() => onAction(product.id, product.name, productValues, product.small_image.url)}
                    >
                        {mainContentImage()}
                    </div>
                ) : (
                    <Link
                        to={!currentColor ? product.url : `${currentUrl}?color=${currentColor.color_id}`}
                        className={classes.productListImage}
                        onClick={() => gtmSelectItem(index)}
                    >
                        {mainContentImage()}
                    </Link>
                )}

                <div className={classes.productListInfo}>
                    <p className={classes.productListBrand}>
                        {product.brand
                            ? typeof product.brand === 'string'
                                ? product.brand.toUpperCase()
                                : product.brand
                            : 'BRAND'}
                    </p>
                    <p className={['h2', classes.productListTitle].join(' ')} title={productName} itemProp="name">
                        {onAction ? (
                            <div
                                onClick={() =>
                                    onAction(product.id, product.name, productValues, product.small_image.url)
                                }
                            >
                                {productName ? productName.toLowerCase() : 'Description'}
                            </div>
                        ) : (
                            <Link to={product.url} onClick={() => gtmSelectItem(index)}>
                                {productName ? productName.toLowerCase() : 'Description'}
                            </Link>
                        )}
                    </p>

                    <span
                        className={classes.productListPrice}
                        itemProp="offers"
                        itemScope
                        itemType="http://schema.org/Offer"
                    >
                        <div
                            itemProp="hasMerchantReturnPolicy"
                            itemScope
                            itemType="https://schema.org/MerchantReturnPolicy"
                        >
                            <meta
                                itemProp="hasMerchantReturnPolicy"
                                content="You have 28 days from receiving your order to request a return."
                            />
                            <meta itemProp="applicableCountry" content="GB" />
                        </div>

                        <link itemProp="availability" href="https://schema.org/InStock" />
                        <meta itemProp="shippingDetails" content={`Free shipping over ${FREE_SHIPPING}`} />
                        {prices.price === 0 ||
                        prices.price_highest === 0 ||
                        prices.price_highest_incl === 0 ||
                        prices.price_incl === 0 ? (
                            <b>Please ask for price</b>
                        ) : (
                            <>
                                <b
                                    className={classnames(classes.standardPrice, {
                                        [classes.crossedOutPrice]: showDiscountPrice,
                                    })}
                                >
                                    <Price
                                        price={
                                            showDiscountPrice ? prices.previous_prices?.price_exl || 0 : prices.price
                                        }
                                        priceIncl={
                                            showDiscountPrice
                                                ? prices.previous_prices?.price_inc || 0
                                                : prices.price_incl
                                        }
                                        priceHighest={!showDiscountPrice ? prices.price_highest : undefined}
                                        priceHighestIncl={!showDiscountPrice ? prices.price_highest_incl : undefined}
                                        vat={vat}
                                        crossedOutPrice={showDiscountPrice}
                                    />
                                </b>{' '}
                                {!showDiscountPrice && <VatInfo />}
                                {showDiscountPrice && (
                                    <div className={classes.productListSpecialPrice}>
                                        <b>
                                            <Price price={prices.price} priceIncl={prices.price_incl} vat={vat} />
                                        </b>
                                        <VatInfo />
                                    </div>
                                )}
                            </>
                        )}
                    </span>

                    <ProductListColourPicker
                        onChange={setCurrentColor}
                        onDefaultColorLoad={setDefaultColor}
                        colorSelections={colorSelections}
                        setOptions={setOptions}
                        productId={product.id}
                    />
                </div>

                {!algolia && tagsAggregation.length > 0 && filteredTags && filteredTags.length > 0 && (
                    <div className={classes.tagsContainer}>
                        {filteredTags?.map((tag) => (
                            <RenderTag
                                key={tag}
                                tag={tag}
                                tagsAggregation={tagsAggregation}
                                onChange={onChange}
                                tagsSelected={tagsSelected}
                            />
                        ))}
                    </div>
                )}

                {algolia && (
                    <div className={classes.tagsContainer}>
                        {filteredTags?.map((tag) => (
                            <AlgoliaTag tag={tag} key={tag} />
                        ))}
                    </div>
                )}
            </div>
        </li>
    );
};
